// Axios client cho cnvwork-saas API. Tự động:
//   - Authorization: Bearer <jwt> (từ authStore)
//   - Idempotency-Key cho mọi mutation (ADR-008)
//   - Refresh token khi 401 (singleton promise — concurrent 401 reuse cùng 1 refresh)
//   - 429 Retry-After backoff (max 1 retry)
//   - Auth-broken circuit (Chunk B 2026-05-28): sau khi refresh fail → set
//     _authBroken=true, mọi request mới REJECT trước khi gửi network. Tránh
//     loop polling/realtime spam BE sau khi token đã chết.
//   - Toast error cho domain errors
//   - Accept-Language = ngôn ngữ đang hiển thị (câu lỗi BE ở cửa chưa đăng nhập)

import { readDeviceTrust } from './deviceTrust';
import axios from 'axios';
import toast from 'react-hot-toast';
import { getLocale } from '@cnv/ui/i18n';
import { useAuthStore } from './authStore';

function newIdempotencyKey() {
  return crypto.randomUUID();
}

// Export: hub.cnvwork.com KHÔNG proxy /api (nginx chỉ serve SPA + /ws) nên mọi fetch() dùng
// đường dẫn TƯƠNG ĐỐI sẽ rơi vào static server của hub và không bao giờ tới BE. Ai gọi fetch
// trực tiếp (beacon, keepalive flush) PHẢI ghép baseURL này.
export const baseURL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

export const inboxClient = axios.create({
  baseURL,
  timeout: 30_000,
});

// ─── Reference-data cache (perf) ────────────────────────────────────────────
// /users/simple gọi ở ~50 file, /teams/scoped ~11, + vài config CRM — đều là data
// ÍT ĐỔI nhưng fetch lại mỗi page/modal. Cache transparent (TTL 60s) + in-flight dedup:
// nhiều component mount cùng lúc → CHỈ 1 request. Whitelist path chính xác (an toàn — request
// khác KHÔNG cache). Mọi lệnh GHI thành công tự xoá cache (interceptor response bên dưới) — trước 26/10/2026 không nơi
// nào gọi clearReferenceCache() ⇒ trang Nguồn lưu xong tải lại vẫn nhận bản cũ tới 60s (gán nguồn cha mà không thấy con).
const REF_CACHE_TTL_MS = 60_000;
const REF_CACHE_PATHS = new Set([
  '/users/simple', '/teams/scoped', '/crm/sources', '/crm/pipelines',
  '/crm/industries', '/crm/deal-roles', '/crm/product-categories',
]);
const _refCache = new Map();    // key → { response, expiry }
const _refInflight = new Map(); // key → Promise
export function clearReferenceCache() { _refCache.clear(); _refInflight.clear(); }
const _origGet = inboxClient.get.bind(inboxClient);
inboxClient.get = function cachedGet(url, config) {
  if (!REF_CACHE_PATHS.has(url)) return _origGet(url, config);
  const key = url + '?' + JSON.stringify(config?.params || {});
  const now = Date.now();
  const hit = _refCache.get(key);
  if (hit && hit.expiry > now) return Promise.resolve(hit.response);
  if (_refInflight.has(key)) return _refInflight.get(key);          // dedup burst on-load
  const p = _origGet(url, config)
    .then((r) => { _refCache.set(key, { response: r, expiry: now + REF_CACHE_TTL_MS }); _refInflight.delete(key); return r; })
    .catch((e) => { _refInflight.delete(key); throw e; });
  _refInflight.set(key, p);
  return p;
};

// ─── Singleton refresh state (Chunk B) ──────────────────────────
// `_refreshing` — Promise<accessToken> | null. Concurrent 401 đợi cùng promise.
// `_authBroken` — sau khi refresh fail HOẶC có lệnh logout. Mọi request mới reject
//   trực tiếp, KHÔNG gửi network. Reset = false khi user login lại thành công.
// `_refreshListeners` — chatStomp / push subscribe sau refresh để recompute headers.
let _refreshing = null;
let _authBroken = false;
const _refreshListeners = new Set();
const _authBrokenListeners = new Set();

export function isAuthBroken() { return _authBroken; }

/** Subscribe — fn(newAccessToken) khi refresh thành công. Return unsubscribe. */
export function onTokenRefreshed(fn) {
  _refreshListeners.add(fn);
  return () => _refreshListeners.delete(fn);
}

/** Subscribe — fn() khi _authBroken transition false → true. Return unsubscribe. */
export function onAuthBroken(fn) {
  _authBrokenListeners.add(fn);
  return () => _authBrokenListeners.delete(fn);
}

// ─── Phân loại lỗi refresh (2026-07-25) ──────────────────────────────────────
// GỐC BUG "SaaS logout hoài": trước đây `catch (e) { _markAuthBroken() }` bắt MỌI lỗi — mất
// mạng, laptop ngủ dậy, 502 khi deploy (đo prod: 81 cụm 502/15 ngày do `pm2 restart` không
// drain), preflight CORS fail (hub.cnvwork.com gọi api.cnvwork.com cross-origin nên MỌI refresh
// đều có OPTIONS đi trước) — rồi XOÁ luôn refresh token. Một lần trượt mạng = mất phiên vĩnh
// viễn, F5 cũng không cứu. Đo prod 14 ngày: 258 lần đăng nhập lại nhưng chỉ 88 lượt 401 trên
// /auth/refresh ⇒ ~78% lần bị đá không hề có BE từ chối.
//
// Luật mới: CHỈ huỷ phiên khi server THẬT SỰ từ chối token (401/403). Mọi thứ khác là lỗi tạm
// → retry backoff, GIỮ NGUYÊN localStorage. Mobile đã làm đúng như vậy từ 2026-05-20
// (cnvwork-mobile/lib/core/api/auth_interceptor.dart:150) — đây là port lại cho web.
export class RefreshTransientError extends Error {
  constructor(reason, cause) {
    super('refresh_transient');
    this.name = 'RefreshTransientError';
    this.reason = reason;             // network_error | timeout | http_5xx | rate_limited
    this.cause = cause;
  }
}

/** Phân loại 1 lỗi axios thành reason chuẩn (khớp enum BE POST /auth/session-lost). */
function _classifyRefreshError(e) {
  const status = e?.response?.status;
  if (status === 401) return 'http_401';
  if (status === 403) return 'http_403';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'http_5xx';
  if (e?.code === 'ECONNABORTED' || /timeout/i.test(e?.message || '')) return 'timeout';
  // Không có response = request không tới nơi: mất mạng, DNS, TLS reset, HOẶC preflight CORS fail.
  // Trình duyệt cố tình không phân biệt được preflight-fail với network-fail (bảo mật) → gộp.
  if (!e?.response) return 'network_error';
  return 'unknown';
}

/** Chỉ 401/403 mới là "server từ chối token" — phần còn lại là hạ tầng. */
function _isTokenRejected(reason) {
  return reason === 'http_401' || reason === 'http_403';
}

// Client RIÊNG cho /auth/refresh: có timeout (axios mặc định = 0 = chờ vô hạn) và KHÔNG dính
// interceptor của inboxClient (tránh đệ quy refresh-trong-refresh).
const authClient = axios.create({ baseURL, timeout: 15_000 });

const REFRESH_RETRY_DELAYS_MS = [1000, 3000, 6000];

const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Singleton refresh — concurrent 401 reuse cùng 1 promise.
 *
 * Throw `RefreshTransientError` khi lỗi hạ tầng (phiên VẪN CÒN, caller nên coi như lỗi mạng),
 * throw lỗi gốc khi server từ chối token (phiên đã chết → đã _markAuthBroken).
 * Export để chatStomp + ws clients dùng chung lock thay vì self-implement.
 */
export async function refreshAccessToken() {
  if (_authBroken) throw new Error('auth_broken');
  if (_refreshing) return _refreshing;

  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) {
    _markAuthBroken('no_refresh_token');
    throw new Error('no_refresh_token');
  }

  _refreshing = (async () => {
    let lastReason = 'unknown';
    let lastErr = null;
    try {
      for (let attempt = 0; attempt <= REFRESH_RETRY_DELAYS_MS.length; attempt += 1) {
        try {
          // supports_rotation: khai với BE rằng client này CHỊU lưu refresh token mới (xử lý ngay
          // dưới đây). Không khai thì BE không rotate — xem api schemas.refreshInput.
          const resp = await authClient.post('/auth/refresh', { refreshToken, supports_rotation: true });
          const newToken = resp.data.accessToken;
          useAuthStore.getState().setSession({
            accessToken: newToken,
            // BE rotate refresh token (sliding window) → PHẢI ghi đè bản đang lưu, nếu không
            // lần refresh sau vẫn gửi token cũ và rơi ra ngoài grace window. Response cũ không
            // có field này → authStore giữ token hiện tại (tương thích 2 chiều).
            refreshToken: resp.data.refreshToken,
            user: resp.data.user,
            active_tenant: resp.data.active_tenant,
          });
          for (const fn of _refreshListeners) { try { fn(newToken); } catch {} }
          return newToken;
        } catch (e) {
          lastErr = e;
          lastReason = _classifyRefreshError(e);
          // Server từ chối token → phiên chết thật, không retry.
          if (_isTokenRejected(lastReason)) {
            _markAuthBroken(lastReason, { http_status: e?.response?.status ?? null });
            throw e;
          }
          const delay = REFRESH_RETRY_DELAYS_MS[attempt];
          if (delay == null) break;                 // hết lượt retry
          await _sleep(delay);
        }
      }
      // Hết retry mà vẫn lỗi hạ tầng: GIỮ PHIÊN. Đây là điểm khác biệt cốt lõi so với code cũ.
      throw new RefreshTransientError(lastReason, lastErr);
    } finally {
      _refreshing = null;
    }
  })();

  return _refreshing;
}

/**
 * Huỷ phiên + về /login. CHỈ gọi khi phiên thật sự không dùng được nữa.
 * @param {string} reason  khớp enum BE (http_401|http_403|no_refresh_token|account_disabled|...)
 */
function _markAuthBroken(reason = 'unknown', extra = {}) {
  if (_authBroken) return;
  _authBroken = true;
  _reportSessionLost(reason, extra);
  // FORCE clear localStorage — tránh stale refreshToken phục hồi sau F5.
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('cnvwork-saas-auth');
    }
  } catch {}
  useAuthStore.getState().logout();
  for (const fn of _authBrokenListeners) { try { fn(); } catch {} }
  // Trang công khai của luồng xác thực — KHÔNG đá đi đâu cả.
  //
  // Người dùng vào /forgot-password thường là NGAY SAU khi bị đăng xuất, nên localStorage
  // còn token cũ đã hỏng. Nếu bất kỳ request nền nào chạm _markAuthBroken thì họ bị bắn về
  // /login giữa lúc đang đặt lại mật khẩu — trông y như "bấm link trong mail thì về trang
  // đăng nhập". /reset-password còn tệ hơn: token nằm ở query, redirect là MẤT LUÔN link.
  const PUBLIC_AUTH_PATHS = ['/login', '/forgot-password', '/reset-password'];
  if (typeof window !== 'undefined' && !PUBLIC_AUTH_PATHS.includes(window.location.pathname)) {
    toast.error('Phiên đăng nhập hết hạn — đăng nhập lại', { duration: 3000 });
    // .replace() — không add history entry để user back button không quay lại trang lỗi
    window.location.replace('/login');
  }
}

// Beacon "phiên bị mất" → BE đếm theo reason (POST /auth/session-lost, 204, không auth).
// KHÔNG await, KHÔNG để lỗi ở đây chặn logout.
//
// TẠI SAO fetch(keepalive) MÀ KHÔNG navigator.sendBeacon: prod build có
// VITE_API_BASE_URL=https://api.cnvwork.com/api/v1 (admin/.env.production) nên đây là request
// CROSS-ORIGIN từ hub.cnvwork.com. sendBeacon chỉ gửi được content-type trong danh sách an toàn
// CORS — 'application/json' sẽ cần preflight mà sendBeacon không làm được ⇒ bị chặn im lặng.
// fetch với keepalive:true làm được preflight VÀ sống sót qua điều hướng
// (window.location.replace chạy ngay mấy dòng dưới).
function _reportSessionLost(reason, extra = {}) {
  // Tách try: parse tuổi token hỏng thì vẫn PHẢI gửi được beacon (mất beacon = mất đúng thứ
  // mình dựng endpoint này để đo).
  let ageSec = null;
  try {
    const rt = useAuthStore.getState().refreshToken;
    if (rt) {
      const payload = JSON.parse(atob(rt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (payload?.iat) ageSec = Math.max(0, Math.round(Date.now() / 1000 - payload.iat));
    }
  } catch { /* không đọc được tuổi token — vẫn gửi beacon với age null */ }
  try {
    const body = JSON.stringify({
      reason,
      client_type: _clientType(),
      refresh_token_age_sec: ageSec,
      http_status: extra.http_status ?? null,
      path: typeof window !== 'undefined' ? window.location.pathname.slice(0, 200) : null,
    });
    fetch(`${baseURL}/auth/session-lost`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      mode: 'cors',
    }).catch(() => {});
  } catch { /* telemetry KHÔNG bao giờ được làm hỏng luồng logout */ }
}

function _clientType() {
  try {
    if (/Electron/i.test(navigator.userAgent)) return 'desktop';
    return 'web';
  } catch { return 'web'; }
}

/** Reset _authBroken sau khi login lại — gọi từ AuthContext sau setSession thành công. */
export function resetAuthBroken() {
  _authBroken = false;
}

// ─── Endpoints được phép gọi khi _authBroken=true (public + login) ────
const AUTH_BROKEN_ALLOWLIST = [
  // '/auth/logout': người dùng đang RỜI ĐI — tuyệt đối không cho nó kích proactive refresh
  // (ladder retry tới ~70s/lượt × 3 lần retry transient = bấm Đăng xuất mà app đứng vài phút).
  '/auth/login', '/auth/google', '/auth/refresh', '/auth/logout',
  '/auth/apple/', '/auth/zalo', '/auth/identity/',
  // Quên mật khẩu: người dùng CHƯA đăng nhập (hoặc token đã hỏng nên mới phải đặt lại).
  // Thiếu dòng này thì proactive-refresh nhảy vào trước, ladder retry ~70s rồi mới gọi —
  // bấm "Gửi liên kết" xong trang đứng cả phút mà không có lỗi nào hiện ra.
  '/auth/password/',
  '/onboarding/', '/public/',
];

function _isAllowedWhenBroken(url = '') {
  return AUTH_BROKEN_ALLOWLIST.some(p => url.startsWith(p) || url.includes(p));
}

// Decode `exp` (ms) từ JWT access token. null nếu không parse được.
function _tokenExpMs(token) {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json);
    return payload?.exp ? payload.exp * 1000 : null;
  } catch { return null; }
}

// Token đã/sắp hết hạn trong < 30s → cần refresh TRƯỚC khi gửi request.
// Không decode được exp → false (để 401-retry phía response lo, tránh refresh thừa).
function _isTokenExpiringSoon(token) {
  const expMs = _tokenExpMs(token);
  if (!expMs) return false;
  return Date.now() >= expMs - 30_000;
}

/**
 * ensureFreshToken — trả access token còn hạn, refresh CHỦ ĐỘNG nếu sắp hết hạn.
 * Dùng chung cho mọi axios client (inboxClient, chatApi._client) + socket → 1 nguồn
 * token authority duy nhất, KHÔNG mỗi client tự xử lý refresh lệch nhau.
 * @returns {Promise<string|null>} token mới/đang dùng, null nếu auth broken / refresh fail.
 */
export async function ensureFreshToken() {
  if (_authBroken) return null;
  const current = useAuthStore.getState().accessToken;
  let token = current;
  if (token && _isTokenExpiringSoon(token)) {
    try {
      token = await refreshAccessToken();
    } catch (e) {
      // Lỗi hạ tầng → phiên VẪN CÒN. Trả token hiện tại (có thể đã hết hạn) thay vì null: caller
      // gửi đi và cùng lắm nhận 401 từ BE rồi tự retry, KHÔNG ai bị đăng xuất. Trả null ở đây
      // từng làm chat/socket rơi vào nhánh "không có token" mỗi lần mạng chớp.
      if (e instanceof RefreshTransientError) return current || null;
      return null;
    }
  }
  return token || null;
}

// POST lô «Nạp nhiều CV» — hạn chờ dài + không tự gửi lại (xem hai interceptor dưới).
const BULK_CV_UPLOAD_RE = /\/recruitment\/campaigns\/[^/]+\/cv-intake\/?$/;

inboxClient.interceptors.request.use(async (config) => {
  // Chunk B: short-circuit khi auth đã broken — không gửi network.
  // Tiết kiệm bandwidth + tránh loop polling spam BE sau logout.
  if (_authBroken && !_isAllowedWhenBroken(config.url || '')) {
    const err = new Error('auth_broken');
    err.code = 'AUTH_BROKEN';
    err.config = config;
    err.response = { status: 401, data: { error: { code: 'auth_broken' } } };
    return Promise.reject(err);
  }

  let token = useAuthStore.getState().accessToken;

  // PROACTIVE REFRESH (2026-05-28): access token TTL 15p → trước đây mỗi 15p các poll
  // (sidebar badges, voice/online-count, chat/config...) bắn với token HẾT HẠN → burst
  // 401 → refresh → retry, lặp "hoài" gây spam console + tải BE. Giờ: nếu token sắp hết
  // hạn → refresh TRƯỚC (singleton, concurrent reuse 1 promise) → request đi với token mới
  // → KHÔNG còn 401. Skip auth endpoints (login/refresh tự lo).
  if (token && !_isAllowedWhenBroken(config.url || '') && _isTokenExpiringSoon(token)) {
    try {
      token = await refreshAccessToken();
    } catch (err) {
      if (err instanceof RefreshTransientError) {
        // Lỗi HẠ TẦNG, phiên vẫn còn. Reject dưới dạng lỗi mạng (KHÔNG có `response`) để rơi
        // vào lưới auto-retry transient của response interceptor bên dưới + hiện overlay
        // "Kết nối Internet không ổn định". TUYỆT ĐỐI không giả 401 ở đây — đó chính là đường
        // biến một lần rớt wifi thành đăng xuất.
        const e = new Error('refresh_transient');
        e.code = 'REFRESH_TRANSIENT';
        e.config = config;
        return Promise.reject(e);
      }
      // Token bị server từ chối → refreshAccessToken đã _markAuthBroken + redirect /login.
      const e = new Error('auth_broken');
      e.code = 'AUTH_BROKEN';
      e.config = config;
      e.response = { status: 401, data: { error: { code: 'auth_broken' } } };
      return Promise.reject(e);
    }
  }

  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Ngôn ngữ ĐANG HIỂN THỊ, đọc lúc GỬI — đổi ngôn ngữ giữa chừng thì request sau đổi theo.
  // Cửa chưa đăng nhập (đăng nhập, quên/đặt lại mật khẩu, nhận lời mời) không có JWT nên máy chủ
  // chọn ngôn ngữ câu lỗi theo header này (api/src/shared/i18nBe.js `localeOf`). Không gắn thì
  // trình duyệt tự gửi ngôn ngữ của MÁY: người chọn tiếng Việt trên Chrome tiếng Anh nhận câu
  // tiếng Anh. Có JWT thì claim trong token thắng, header vô hại. Chỗ gọi tự đặt header thì giữ.
  if (!config.headers.has('Accept-Language')) {
    config.headers.set('Accept-Language', getLocale());
  }

  const isMutation = ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase());
  if (isMutation && !config.headers['Idempotency-Key']) {
    config.headers['Idempotency-Key'] = newIdempotencyKey();
  }
  // «Nạp nhiều CV» (cnv-ui BulkCvIntakeModal) gửi một lượt tới 50 tệp CV × 10MB: mạng chậm là quá hạn 30s mặc định
  // khi máy chủ còn đang nhận dở ⇒ báo lỗi giả. Chờ 5 phút; và KHÔNG tự gửi lại (xem `_isUpload` ở response).
  if (config.method?.toLowerCase() === 'post' && BULK_CV_UPLOAD_RE.test(config.url || '') && (config.timeout || 0) < 300_000) {
    config.timeout = 300_000;
  }
  return config;
});

/** Số giây chờ → chuỗi người đọc được. Bucket ngày có thể trả tới 86400s: "1440 phút" là
 *  con số vô nghĩa với user, phải quy ra giờ. */
function formatWait(sec) {
  if (sec >= 7200) return `${Math.ceil(sec / 3600)} giờ`;
  if (sec >= 120) return `${Math.ceil(sec / 60)} phút`;
  return `${sec} giây`;
}

inboxClient.interceptors.response.use(
  (res) => {
    // Ghi xong ⇒ dữ liệu tham chiếu có thể đã đổi (thêm nguồn, sửa ngành…) — bỏ cache để lần đọc sau lấy bản mới.
    const _m = String(res.config?.method || 'get').toLowerCase();
    if (_m !== 'get' && _m !== 'head' && _m !== 'options') clearReferenceCache();
    // Request thành công sau khi đã retry transient → ẩn overlay "Kết nối Internet không ổn định".
    if (res.config?._retryCount > 0) {
      try { window.dispatchEvent(new CustomEvent('server-retry-resolved')); } catch {}
    }
    return res;
  },
  async (err) => {
    const original = err.config;
    if (!original) return Promise.reject(err);

    // ───── Auto-retry lỗi tạm thời (mất mạng / server bận) + overlay ─────
    // !response = network/timeout; 502/503/504 = server bận. Retry tối đa 3 lần × 3s.
    // Bắn 'server-retry' → ServerRetryOverlay hiện "Kết nối Internet không ổn định".
    // Skip upload (retry = upload lại bytes) — fail nhanh để toast.
    const _st = err.response?.status;
    // 502/503/504 chỉ được coi là "server bận" khi KHÔNG có lỗi nghiệp vụ có cấu trúc.
    // BE trả {error:{code}} nghĩa là request đã tới nơi và bị từ chối có lý do → retry
    // 3 lần là gửi lại thao tác 3 lần (vd yêu cầu xin quyền gọi Zalo) và bung overlay
    // "Kết nối Internet không ổn định" dù mạng vẫn tốt.
    const _hasDomainError = !!err.response?.data?.error?.code;
    const _isTransient = (!err.response || _st === 502 || _st === 503 || _st === 504) && !_hasDomainError;
    // «Nạp nhiều CV» cũng là tải tệp: gửi lại = tải lại cả xấp + mỗi CV một lượt AI tính tiền nữa.
    const _isUpload = /\/(storage\/upload|files\/upload)/.test(original.url || '')
      || (original.method?.toLowerCase() === 'post' && BULK_CV_UPLOAD_RE.test(original.url || ''));
    // Đường AI tuyển dụng: mỗi lượt gọi hãng Claude tốn TIỀN THẬT và mất 30-60s. Timeout ở
    // trình duyệt KHÔNG huỷ được lượt đã bay sang hãng ⇒ thử lại = trả tiền lần nữa cho đúng
    // việc máy chủ đang làm dở. Đo Core 16/09/2026: một cú bấm «AI lập tiêu chí» thành BA
    // lượt gọi hãng cách nhau ~32s. Các đường này tự đặt timeout 180s (cnv-ui
    // lib/recruitmentAi.js AI_CALL_TIMEOUT_MS) nên không cần lưới retry nữa.
    // Hộp thư «AI rep giúp» / «Gợi ý AI» cũng là lượt gọi hãng (đo Core 16/09/2026: 18,5s cho
    // một bản nháp ở agent có kho tri thức) ⇒ cùng luật: hạn chờ 180s, không retry.
    const _isAiCall = /\/recruitment\/(ai\/|campaigns\/[^/]+\/ai-criteria|candidates\/[^/]+\/ai-(screen|attach))|\/ai-(draft|suggest)-reply/.test(original.url || '');
    if (_isTransient && !_isUpload && !_isAiCall) {
      if (original._retryCount == null) original._retryCount = 0;
      if (original._retryCount < 3) {
        original._retryCount++;
        try { window.dispatchEvent(new CustomEvent('server-retry', { detail: { attempt: original._retryCount } })); } catch {}
        await new Promise(r => setTimeout(r, 3000));
        return inboxClient(original);
      }
      try { window.dispatchEvent(new CustomEvent('server-retry-resolved')); } catch {}
    }

    // ───── Chunk B: 429 Retry-After backoff (max 1 retry) ─────
    // Chunk A BE trả Retry-After header → FE đợi đúng số giây rồi retry 1 lần.
    // Cap 30s tránh stuck UI quá lâu. Không retry mutation idempotent-conflict.
    if (err.response?.status === 429 && !original._retried429) {
      original._retried429 = true;
      const retryAfter = parseInt(err.response.headers?.['retry-after'], 10);
      if (Number.isFinite(retryAfter) && retryAfter > 0 && retryAfter <= 30) {
        await new Promise(r => setTimeout(r, retryAfter * 1000));
        return inboxClient(original);
      }
      // Header missing hoặc bất hợp lý → toast + reject.
      // `id` cố định = react-hot-toast THAY THẾ toast cũ thay vì xếp chồng. Bắt buộc: 1 lần
      // mở app bắn hàng chục request song song, dính rate limit là dính CẢ LOẠT → không dedupe
      // thì user thấy tường 15-20 toast đỏ giống hệt nhau che kín màn hình (sự cố 2026-07-29).
      const waitSec = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null;
      toast.error(
        waitSec && waitSec > 30
          ? `Hệ thống đang giới hạn truy cập — thử lại sau ${formatWait(waitSec)}`
          : 'Thao tác quá nhanh — thử lại sau vài giây',
        { duration: 4000, id: 'rate_limited' },
      );
      return Promise.reject(err);
    }

    // ───── 401 → singleton refresh + retry (Chunk B refactor) ─────
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      const reqUrl = original.url || '';
      // /auth/refresh tự fail 401 → refresh token chết thật → auth broken. KHÔNG retry chính nó.
      if (reqUrl.endsWith('/auth/refresh')) {
        _markAuthBroken('http_401', { http_status: 401 });
        return Promise.reject(err);
      }
      // Auth-flow endpoints khác (login/google/zalo/apple/identity/onboarding/public) trả 401
      // = SAI credential / OTP hết hạn, KHÔNG phải session expired. TUYỆT ĐỐI không kích refresh
      // circuit — nếu không 1 lần nhập sai mật khẩu sẽ gọi refreshAccessToken() → không có
      // refreshToken → _markAuthBroken (toast "hết phiên" + redirect /login + throw 'auth_broken'
      // unhandled cho các request batched đang chạy). Để LoginPage/SignupPage tự map lỗi
      // credential. (GlitchTip issue 8 — Error: auth_broken onunhandledrejection)
      if (_isAllowedWhenBroken(reqUrl)) {
        return Promise.reject(err);
      }
      try {
        const newToken = await refreshAccessToken();
        original.headers.Authorization = `Bearer ${newToken}`;
        return inboxClient(original);
      } catch (e) {
        if (e instanceof RefreshTransientError) {
          // Không refresh được vì hạ tầng — GIỮ PHIÊN. Rejection này phát sinh BÊN TRONG response
          // interceptor nên không quay lại được lưới transient ở trên; bắn overlay để user thấy
          // "Kết nối Internet không ổn định" thay vì thao tác im lặng không phản hồi.
          try { window.dispatchEvent(new CustomEvent('server-retry', { detail: { attempt: 1 } })); } catch {}
          const t = new Error('refresh_transient');
          t.code = 'REFRESH_TRANSIENT';
          t.config = original;
          return Promise.reject(t);
        }
        // _markAuthBroken đã được gọi trong refreshAccessToken nếu token bị từ chối
        return Promise.reject(err);
      }
    }

    // Toast domain error — map sang tiếng Việt friendly thay vì show raw code (TC-183).
    // SKIP auto-toast cho các endpoint /auth/* (login, google, refresh, onboarding) —
    // LoginPage + SignupPage tự handle error với mapping cụ thể của riêng nó. Nếu interceptor
    // toast trước thì user sẽ thấy 2 toast (raw + friendly) như case TC-219.
    const url = original?.url || '';
    const isAuthFlow = /\/(auth|onboarding)\//.test(url) || url.endsWith('/auth/login') || url.endsWith('/auth/google') || url.endsWith('/auth/refresh');
    const code = err.response?.data?.error?.code;
    const message = err.response?.data?.error?.message;
    const details = err.response?.data?.error?.details;
    // module_disabled: điều kiện BÌNH THƯỜNG khi tenant không mua module (vd team_chat).
    // Forbidden(scope,msg) → code='scope_required' + details.scope='module_disabled'
    // (api/src/shared/errors.js). PHẢI check details.scope, KHÔNG phải code. Caller global
    // (TeamChatPushNotifications, unread badge...) đã .catch — KHÔNG toast message kỹ thuật
    // "Module team_chat chưa enable..." cho end user. (TC-321/326/328/362)
    // file_deleted: tệp đã xoá khi dọn dẹp dung lượng — chỗ hiển thị tự thay bằng ô «Tệp đã bị xoá»;
    // toast thì một hội thoại nhiều ảnh cũ = một tường toast đỏ.
    const SKIP_TOAST_CODES = ['idempotency_conflict', 'auth_broken', 'file_deleted'];
    const isModuleDisabled = details?.scope === 'module_disabled';

    // ───── Bị khoá ở workspace ĐANG mở (2026-09-07) ─────
    // Khoá là theo workspace, không phải khoá tài khoản: người này có thể còn workspace khác đang
    // mở. Đá về /login là sai (họ vẫn đăng nhập được) mà để nguyên tại chỗ cũng sai — mọi thao tác
    // sẽ ăn 403 rời rạc. Đưa thẳng ra màn chọn workspace, kèm câu của BE (có TÊN workspace).
    // Không đụng phiên: token vẫn hợp lệ, /auth/me/tenants + switch-tenant vẫn chạy được.
    if (code === 'workspace_locked' && typeof window !== 'undefined') {
      const beMsg = typeof err.response?.data?.message === 'string' ? err.response.data.message : null;
      toast.error(beMsg || 'Bạn đang bị khoá ở workspace này', { duration: 5000, id: 'workspace_locked' });
      if (window.location.pathname !== '/select-workspace') {
        window.location.replace('/select-workspace');
      }
      return Promise.reject(err);
    }
    // ─── Chống 2 toast CÙNG MỘT CÂU cho lỗi của thao tác ghi ───
    // Component cnv-ui nào cũng tự `toast.error(err.response.data.message || 'Lỗi ...')` trong
    // catch, mà `data.message` (BE đẩy ra khi lỗi có `userMessage`, đã dịch theo ngôn ngữ người
    // dùng — api/src/middleware/errorHandler.js) cũng chính là câu interceptor bắn. Toast thêm =
    // user thấy ĐÚNG một câu HAI lần: ảnh khách gửi 31/07, rồi 14/09 lan sang mọi 403 mà đợt vá
    // IDOR gắn câu (0639c079 — Hộp thư, chuyển giao dữ liệu CRM, chia sẻ đơn...).
    //
    // Nhường cho component, NHƯNG KHÔNG nhường mù: cnv-ui còn ~300 `catch {}` (đo bằng
    // tools/scan-fe-dead-handlers.js) nên nhường thẳng là có đường thành im lặng hoàn toàn —
    // đúng thứ feedback_no_silent_fail_fe cấm. Cách làm: hoãn 250ms rồi soi vùng toast xem đã có
    // toast chứa ĐÚNG câu mình sắp bắn chưa (`isToastShowing`). Có rồi thì thôi, chưa có thì tự bắn.
    // - So bằng CHÍNH CÂU, không bằng cụm chữ ký: bản trước dò "không phải lỗi hệ thống" ⇒ người
    //   dùng en/fr không bao giờ khớp nên vẫn 2 toast, và chỉ phủ mỗi `permission_denied`.
    // - Không "có toast nào đó": toast success cũ còn nằm đó sẽ làm nhận nhầm.
    // - Áp cho MỌI code của mutation: câu so là `text` bên dưới, nên lỗi chưa có `data.message` mà
    //   component đọc `error.message` (vd `scope_required` kèm câu — formatErrorForUser trả nguyên
    //   câu đó) cũng hết đúp.
    // GET giữ nguyên đường cũ (caller load list/badge phần lớn im lặng, mất toast là 403 lúc mở
    // trang không còn dấu vết).
    const isMutationReq = ['post', 'put', 'patch', 'delete'].includes(String(original?.method || '').toLowerCase());
    const beUserMessage = typeof err.response?.data?.message === 'string' ? err.response.data.message : null;
    // Opt-out per-request: caller nào tự map lỗi domain sang câu tiếng Việt riêng
    // thì truyền { skipErrorToast: true } — nếu không user thấy 2 toast (raw code
    // của interceptor + câu friendly của caller). Cùng lý do với isAuthFlow ở trên,
    // nhưng không phải hardcode thêm URL vào interceptor mỗi lần có case mới.
    // Truyền MẢNG mã lỗi khi trang chỉ tự hiện riêng mấy lỗi đó, vd `['external_api_disabled']` ở
    // trang API Keys / Webhooks (khung «Kết nối API đang tắt» thay cho toast thô «org chưa bật external
    // API access», 22/10/2026) — lỗi khác (500, thiếu quyền) vẫn toast như thường.
    const skipOpt = original?.skipErrorToast;
    const callerSkips = Array.isArray(skipOpt) ? skipOpt.includes(code) : !!skipOpt;
    if (code && !SKIP_TOAST_CODES.includes(code) && !isModuleDisabled && !isAuthFlow
        && !callerSkips) {
      // Ưu tiên câu BE viết cho NGƯỜI (`data.message` — errorHandler chỉ đẩy ra khi error có
      // `userMessage`). BE biết ngữ cảnh nên câu luôn sát hơn; `formatErrorForUser` là đường
      // dự phòng cho error chưa khai `userMessage` và cho BE bản cũ chưa deploy.
      const text = beUserMessage || formatErrorForUser({ code, message, details });
      // Riêng lỗi quyền: gắn `id` để dedupe. Mở 1 trang là bắn hàng loạt request, thiếu quyền thì
      // thiếu CẢ LOẠT → không dedupe là phủ kín màn hình bằng cùng một câu (cùng lý do với
      // 'rate_limited' ở trên). Code khác giữ nguyên hành vi cũ, không đụng.
      const dedupeId = (code === 'permission_denied' || code === 'scope_required') ? `domain_${code}` : undefined;
      const opts = dedupeId ? { id: dedupeId } : undefined;
      if (isMutationReq) {
        // Xem "Chống 2 toast" bên trên. `catch {}` bọc ngoài vì đây là nhánh chỉ để KHÔNG mất
        // thông báo — lỗi trong lúc dò DOM tuyệt đối không được làm gãy luồng lỗi gốc.
        setTimeout(() => {
          try {
            if (!isToastShowing(text)) toast.error(text, opts);
          } catch { toast.error(text, opts); }
        }, 250);
      } else {
        toast.error(text, opts);
      }
    }
    return Promise.reject(err);
  },
);

// Trên màn hình đã có toast chứa đúng câu `text` chưa — cho nhánh hoãn "Chống 2 toast" ở trên.
// Chỉ soi toast của react-hot-toast (`[data-rht-toaster]`, ô chữ mang role="status"): role="status"
// còn gặp ở chỗ khác ngoài toast. `textContent` + gộp khoảng trắng vì câu có thể xuống dòng;
// `includes` vì component có thể ghép câu vào một câu dài hơn.
function isToastShowing(text) {
  const norm = (s) => String(s || '').replace(/\s+/g, ' ').trim();
  const want = norm(text);
  if (!want) return false;
  return [...document.querySelectorAll('[data-rht-toaster] [role="status"]')]
    .some((n) => norm(n.textContent).includes(want));
}

// Friendly Vietnamese error formatter.
// - Ưu tiên Zod field issues (details.issues) → "<Field>: <reason>" cụ thể.
// - Fallback theo code (bad_input/unauthorized/forbidden/not_found...) sang text VN.
// Tránh để user thấy raw "bad_input: bad input".
function formatErrorForUser({ code, message, details }) {
  const issues = Array.isArray(details?.issues) ? details.issues : null;
  if (issues && issues.length > 0) {
    const FIELD_LABEL = {
      search: 'Từ khoá tìm kiếm', email: 'Email', password: 'Mật khẩu', name: 'Họ tên',
      phone: 'Số điện thoại', employee_code: 'Mã nhân viên', department: 'Phòng ban',
      team_id: 'Team', role: 'Vai trò', uid: 'UID', limit: 'Số lượng', page: 'Trang',
    };
    const issueText = (it) => {
      const field = it?.path || it?.in || 'trường';
      const label = FIELD_LABEL[field] || field;
      const ic = it?.code;
      const msg = it?.message || '';
      if (ic === 'too_big') {
        const m = msg.match(/at most (\d+)/i);
        return `${label} không được vượt quá ${m ? m[1] : 200} ký tự`;
      }
      if (ic === 'too_small') {
        const m = msg.match(/at least (\d+)/i);
        return `${label} phải có ít nhất ${m ? m[1] : 1} ký tự`;
      }
      if (ic === 'invalid_string' && /email/i.test(msg)) return `${label} không đúng định dạng email`;
      if (ic === 'invalid_type') return `${label} sai kiểu dữ liệu`;
      if (ic === 'invalid_enum_value') return `${label} không nằm trong các giá trị cho phép`;
      if (ic === 'required') return `${label} là bắt buộc`;
      return `${label}${msg ? `: ${msg}` : ' không hợp lệ'}`;
    };
    return issues.slice(0, 3).map(issueText).join(' · ');
  }
  // Lỗi PHÂN QUYỀN — phải nói rõ "thiếu quyền", đừng để user đoán là hệ thống lỗi.
  // Câu chuẩn do BE dựng kèm nhãn ô trên bảng Phân quyền (api/src/shared/permissionLabels.js) và
  // caller ở trên đã ưu tiên nó. Nhánh này chỉ chạy khi KHÔNG có câu đó: BE bản cũ chưa deploy,
  // hoặc gate bằng scope API-key (`scope_required`) còn trả chuỗi kỹ thuật 'requires scope ...'.
  // Cố ý KHÔNG in mã quyền ra cho end user — mã đã có trong `details` cho log/CSKH.
  if (code === 'permission_denied' || code === 'scope_required') {
    const isTechnical = !message || /^requires (permission|scope)\b/i.test(message);
    if (!isTechnical) return message;
    return 'Chưa được cấp quyền cho thao tác này — không phải lỗi hệ thống. '
      + 'Nhờ quản trị viên cấp thêm ở Cài đặt → Phân quyền.';
  }
  const CODE_LABEL = {
    bad_input: 'Dữ liệu nhập vào không hợp lệ',
    unauthorized: 'Phiên đăng nhập đã hết — vui lòng đăng nhập lại',
    forbidden: 'Bạn không có quyền thực hiện thao tác này',
    not_found: 'Không tìm thấy bản ghi',
    conflict: 'Dữ liệu trùng / đã tồn tại',
    rate_limited: 'Thao tác quá nhanh — thử lại sau ít giây',
    rate_limit_exceeded: 'Thao tác quá nhanh — thử lại sau ít giây',
    server_error: 'Hệ thống đang gặp lỗi — vui lòng thử lại sau',
  };
  const friendly = CODE_LABEL[code];
  if (friendly) return friendly;
  return message || code || 'Đã xảy ra lỗi không xác định';
}

export const authApi = {
  // `supports_password_change`: trang đăng nhập này CÓ màn «đặt mật khẩu mới» (cnv-ui LoginChallenge) ⇒ máy chủ được
  // phép trả 403 password_change_required. `device_trust`: thẻ thiết bị tin cậy của email này (lib/deviceTrust.js).
  async login(email, password) {
    const device_trust = readDeviceTrust(email);
    const { data } = await inboxClient.post('/auth/login', {
      email, password, supports_password_change: true, ...(device_trust ? { device_trust } : {}),
    });
    return data;
  },
  // ── Bước hai của đăng nhập (403 kèm error.details.challenge_token) — URL dưới /auth/login… và /auth/password/…
  // đều nằm trong AUTH_BROKEN_ALLOWLIST: đi thẳng, không refresh. Máy chủ trả ĐÚNG phản hồi đăng nhập khi xong.
  async loginMfa(body) {
    const { data } = await inboxClient.post('/auth/login/mfa', body);
    return data;
  },
  async loginMfaEnrollStart(challenge_token) {
    const { data } = await inboxClient.post('/auth/login/mfa/enroll/start', { challenge_token });
    return data.data;
  },
  async loginMfaEnrollConfirm(challenge_token, code, device_trust) {
    const { data } = await inboxClient.post('/auth/login/mfa/enroll/confirm', {
      challenge_token, code, ...(device_trust ? { device_trust } : {}),
    });
    return data;
  },
  async passwordChangeRequired(challenge_token, newPassword, device_trust) {
    const { data } = await inboxClient.post('/auth/password/change-required', {
      challenge_token, newPassword, ...(device_trust ? { device_trust } : {}),
    });
    return data;
  },
  async loginWithGoogle(arg) {
    // arg: string (idToken — legacy GSI) HOẶC object { code } (auth-code flow lấy
    // luôn calendar scope) / { idToken }. BE googleLogin nhận cả idToken lẫn code.
    // User KHÔNG tồn tại → BE trả 403 account_not_registered (no JIT — security).
    const body = typeof arg === 'string' ? { idToken: arg } : (arg || {});
    const { data } = await inboxClient.post('/auth/google', body);
    return data;
  },
  async listMyTenants() {
    const { data } = await inboxClient.get('/auth/me/tenants');
    return data.data;
  },
  // «Tạo workspace» ở màn chọn workspace — mình là chủ, dùng thử 14 ngày. Trả workspace vừa tạo
  // ({ tenant_id, slug, code, display_name… }); caller gọi switchTenant để vào.
  async createWorkspace(name) {
    const { data } = await inboxClient.post('/auth/me/tenants', { name });
    return data.data;
  },
  // Gửi kèm refreshToken hiện tại: refresh claim mang active_tenant_id nên đổi workspace mà
  // KHÔNG rotate refresh token thì ~15 phút sau lần refresh kế tiếp sẽ kéo user về workspace cũ.
  // BE trả refreshToken mới (có thể vắng nếu token legacy) — caller phải lưu lại.
  async switchTenant(tenant_id) {
    const refreshToken = useAuthStore.getState().refreshToken || undefined;
    const { data } = await inboxClient.post('/auth/me/switch-tenant', { tenant_id, refreshToken });
    return data;
  },
  // ── Quên mật khẩu (@cnv/ui ForgotPasswordPage / ResetPasswordPage) ──────────
  // BE luôn trả 202 kể cả email không tồn tại → resolve bình thường, KHÔNG suy ra
  // "email có thật" từ việc không có lỗi.
  async forgotPassword(email) {
    await inboxClient.post('/auth/password/forgot', { email });
  },
  async verifyResetToken(token) {
    const { data } = await inboxClient.get('/auth/password/reset-token', { params: { token } });
    return data;
  },
  async resetPassword(token, newPassword) {
    await inboxClient.post('/auth/password/reset', { token, newPassword });
  },
};

// Xác thực 2 lớp của CHÍNH MÌNH + chính sách workspace (cnv-ui MfaSettingsCard / WorkspaceMfaPolicyCard).
// Sai mã máy chủ trả 400 (không 401) ⇒ không kích refresh.
export const mfaApi = {
  async status() { return (await inboxClient.get('/auth/mfa')).data.data; },
  async setup() { return (await inboxClient.post('/auth/mfa/setup')).data.data; },
  async enable(code) { return (await inboxClient.post('/auth/mfa/enable', { code })).data.data; },
  async disable(code) { return (await inboxClient.post('/auth/mfa/disable', { code })).data.data; },
  async regenerateRecoveryCodes(code) { return (await inboxClient.post('/auth/mfa/recovery-codes', { code })).data.data; },
  async getWorkspacePolicy() { return (await inboxClient.get('/auth/me/workspace-mfa-policy')).data.data; },
  async setWorkspacePolicy(mfa_policy) {
    return (await inboxClient.put('/auth/me/workspace-mfa-policy', { mfa_policy })).data.data;
  },
};

export const inboxApi = {
  async listConversations(params = {}) {
    const { data } = await inboxClient.get('/inbox/conversations', { params });
    return data;
  },
  async getConversation(id) {
    const { data } = await inboxClient.get(`/inbox/conversations/${id}`);
    return data.data;
  },
  async createConversation(input) {
    const { data } = await inboxClient.post('/inbox/conversations', input);
    return data.data;
  },
  async updateStatus(id, status) {
    const { data } = await inboxClient.patch(`/inbox/conversations/${id}/status`, { status });
    return data.data;
  },
  async listMessages(conv_id, params = {}) {
    const { data } = await inboxClient.get(`/inbox/conversations/${conv_id}/messages`, { params });
    return data.data;
  },
  async sendMessage(conv_id, body) {
    const { data } = await inboxClient.post(`/inbox/conversations/${conv_id}/messages`, body);
    return data.data;
  },
};

// channelsApi đã thay bằng cnv-ui ChatChannelsPage gọi trực tiếp api/axios.

// Key MCP — khách tự tạo cho workspace của mình. Chỉ Admin gọi được (BE gác
// requireTenantAdmin); tenant lấy từ JWT nên không truyền tenant_id.
// Màn đồng ý OAuth của đầu nối MCP (/oauth/authorize — pages/OAuthConsentPage.jsx). `req` = mã yêu cầu máy chủ
// cất lúc ứng dụng AI gọi api.cnvwork.com/authorize. approve / deny trả `redirect_url` để đưa trình duyệt về ứng dụng.
export const oauthConsentApi = {
  async get(req) {
    const { data } = await inboxClient.get(`/oauth/requests/${encodeURIComponent(req)}`);
    return data.data;
  },
  async approve(req, scopes) {
    const { data } = await inboxClient.post(`/oauth/requests/${encodeURIComponent(req)}/approve`, { scopes });
    return data.data;
  },
  async deny(req) {
    const { data } = await inboxClient.post(`/oauth/requests/${encodeURIComponent(req)}/deny`, {});
    return data.data;
  },
};

export const mcpKeysApi = {
  async list() {
    const { data } = await inboxClient.get('/mcp-keys');
    return data.data;            // { keys, max_active, max_ttl_days, default_ttl_days }
  },
  async create(body) {
    const { data } = await inboxClient.post('/mcp-keys', body);
    return data.data;            // raw_key CHỈ có ở đây, không lấy lại được
  },
  async revoke(id) {
    const { data } = await inboxClient.delete(`/mcp-keys/${id}`);
    return data.data;
  },
};

// Org chưa bật kết nối API ⇒ list trả 403 `external_api_disabled`: trang tự hiện khung bật (ExternalApiGate),
// toast thêm là đúp câu thô.
const EXTERNAL_API_OFF_SILENT = { skipErrorToast: ['external_api_disabled'] };

export const apiKeysApi = {
  async list() {
    const { data } = await inboxClient.get('/api-keys', EXTERNAL_API_OFF_SILENT);
    return data.data;
  },
  async create(body) {
    const { data } = await inboxClient.post('/api-keys', body);
    return data.data;          // có thể có raw_key + warning
  },
  async revoke(id, reason) {
    const { data } = await inboxClient.post(`/api-keys/${id}/revoke`, { reason });
    return data.data;
  },
};

export const webhooksApi = {
  async list() {
    const { data } = await inboxClient.get('/webhooks', EXTERNAL_API_OFF_SILENT);
    return data.data;
  },
  // Tên sự kiện đăng ký được — máy chủ là nguồn duy nhất (webhookService.VALID_EVENTS), đừng chép tay ở FE.
  async events() {
    const { data } = await inboxClient.get('/webhooks/events');
    return data.data;
  },
  async create(body) {
    const { data } = await inboxClient.post('/webhooks', body);
    return data.data;          // có thể có secret + warning
  },
  async update(id, body) {
    const { data } = await inboxClient.patch(`/webhooks/${id}`, body);
    return data.data;
  },
  async rotateSecret(id) {
    const { data } = await inboxClient.post(`/webhooks/${id}/rotate-secret`, {});
    return data.data;
  },
  async remove(id) {
    await inboxClient.delete(`/webhooks/${id}`);
  },
  async listDeliveries(id, params = {}) {
    const { data } = await inboxClient.get(`/webhooks/${id}/deliveries`, { params });
    return data.data;
  },
  async replayDelivery(delivery_id) {
    const { data } = await inboxClient.post(`/webhook-deliveries/${delivery_id}/replay`, {});
    return data.data;
  },
};

// Chiến dịch gửi tin hàng loạt vào nhóm Zalo cá nhân: lời gọi máy chủ nằm cùng trang ở kernel
// (@cnv/ui/pages/inbox/ZaloCampaignsPage, 14/10/2026) — Core + Work một bản.

export const invitationApi = {
  // Admin (JWT)
  async create(body) {
    const { data } = await inboxClient.post('/users/invitations', body);
    return data.data;
  },
  async list(status) {
    const { data } = await inboxClient.get('/users/invitations', {
      params: status ? { status } : {},
    });
    return data.data;
  },
  async revoke(id, reason) {
    await inboxClient.delete(`/users/invitations/${id}`, {
      data: { reason: reason || null },
    });
  },
  // Public (no JWT). Trang /invite tự hiện lỗi theo mã (AcceptInvitePage) ⇒ tắt toast tự động của
  // interceptor — không tắt thì cùng một lỗi hiện 2 lần (409 từng ra 2 toast "already_member").
  async getInfo(token) {
    const { data } = await inboxClient.get(`/public/invitations/${encodeURIComponent(token)}`,
      { skipErrorToast: true });
    return data.data;
  },
  async redeem(token, body) {
    const { data } = await inboxClient.post(`/public/invitations/${encodeURIComponent(token)}/redeem`, body,
      { skipErrorToast: true });
    return data.data;
  },
  async accept(token, body) {
    const { data } = await inboxClient.post(`/public/invitations/${encodeURIComponent(token)}/accept`, body,
      { skipErrorToast: true });
    return data.data;
  },
  // body: { code } — auth code của nút Google (GoogleCodeButton), email lấy từ Google.
  async acceptWithGoogle(token, body) {
    const { data } = await inboxClient.post(`/public/invitations/${encodeURIComponent(token)}/accept-google`, body,
      { skipErrorToast: true });
    return data.data;
  },
};

// Gói dịch vụ + seat quota của tenant hiện tại (gói Free = 10 user). Dùng cho
// banner seat + disable nút mời + trang Billing (lịch sử license batch).
export const planApi = {
  async seatStatus() {
    const { data } = await inboxClient.get('/users/seat-status');
    return data.data;
  },
};

// Kho ứng dụng — SP đang dùng / chưa mua kèm SỐ LƯỢNG THẬT (license, không phải catalog seed).
// Không có hàm activate: gói trả phí chỉ được kích hoạt bên Core CNV.
export const marketplaceApi = {
  async apps() {
    const { data } = await inboxClient.get('/marketplace/apps');
    return data.data;
  },
  async assignees(key) {
    const { data } = await inboxClient.get(`/marketplace/apps/${encodeURIComponent(key)}/assignees`);
    return data.data;
  },
  async upgradeRequests() {
    const { data } = await inboxClient.get('/marketplace/upgrade-requests');
    return data.data;
  },
  async requestUpgrade(body) {
    const { data } = await inboxClient.post('/marketplace/upgrade-requests', body);
    return data.data;
  },
  async cancelUpgradeRequest(id) {
    const { data } = await inboxClient.delete(`/marketplace/upgrade-requests/${id}`);
    return data.data;
  },
};

// ── TỔNG QUAN DỰ ÁN (port từ CNV Connect 28/08/2026) ──────────────────────────────────────────────────────
// Màn hình danh sách toàn bộ dự án + giai đoạn + bộ lọc đã lưu. BE ở
// `api/src/modules/tasks/routes/projectsOverviewV1.js`.
export const projectsApi = {
  async overview(params) {
    // Trả nguyên gói { data, groups, totals, page, limit } — KHÔNG bóc lấy mỗi `data` như các
    // hàm trên: cộng dồn theo nhóm và dòng tổng là phần chính của màn này, tính ở BE trên
    // TOÀN BỘ tập đã lọc chứ không phải trang đang xem.
    const { data } = await inboxClient.get('/tasks/projects/overview', { params });
    return data;
  },
  async update(id, body) {
    const { data } = await inboxClient.put(`/tasks/projects/${id}`, body);
    return data.data;
  },
  async stages() {
    const { data } = await inboxClient.get('/tasks/project-stages');
    return data.data;
  },
  async createStage(body) {
    const { data } = await inboxClient.post('/tasks/project-stages', body);
    return data.data;
  },
  async updateStage(id, body) {
    const { data } = await inboxClient.put(`/tasks/project-stages/${id}`, body);
    return data.data;
  },
  async deleteStage(id) {
    const { data } = await inboxClient.delete(`/tasks/project-stages/${id}`);
    return data.data;
  },
  // Bộ lọc đã lưu dùng CHUNG đường `/tasks/saved-views` + bảng `task_saved_views` có sẵn của
  // cụm Việc — chỉ khác `module`. Đừng mở đường thứ hai cho cùng một khái niệm.
  async savedViews(module = 'projects') {
    const { data } = await inboxClient.get('/tasks/saved-views', { params: { module } });
    return data.data;
  },
  async saveView({ name, params, is_shared }) {
    const { data } = await inboxClient.post('/tasks/saved-views', {
      module: 'projects', name, view_type: 'list', filters: params || {}, is_public: !!is_shared,
    });
    return data.data;
  },
  async deleteView(id) {
    const { data } = await inboxClient.delete(`/tasks/saved-views/${id}`);
    return data.data;
  },
};

// ── BÀN XẾP VIỆC (port từ CNV Connect 28/08/2026) ──────────────────────────────────────────────────────────────
// BE: `api/src/modules/tasks/routes/workScheduleV1.js`.
export const scheduleApi = {
  async grid(params) {
    // Nguyên gói { data, days, totals } — con số đầu mỗi cột ngày do BE tính trên toàn khoảng.
    const { data } = await inboxClient.get('/work-schedule', { params });
    return data;
  },
  async unscheduled(params) {
    const { data } = await inboxClient.get('/work-schedule/unscheduled', { params });
    return data.data;
  },
  async create(body) {
    const { data } = await inboxClient.post('/work-schedule', body);
    return data.data;
  },
  async update(id, body) {
    const { data } = await inboxClient.put(`/work-schedule/${id}`, body);
    return data.data;
  },
  async remove(id) {
    const { data } = await inboxClient.delete(`/work-schedule/${id}`);
    return data.data;
  },
  async check(body) {
    const { data } = await inboxClient.post('/work-schedule/check', body);
    return data.data;
  },
  async publish(body) {
    const { data } = await inboxClient.post('/work-schedule/publish', body);
    return data.data;
  },
  async copyWeek(body) {
    const { data } = await inboxClient.post('/work-schedule/copy-week', body);
    return data.data;
  },
  async suggest(params) {
    const { data } = await inboxClient.get('/work-schedule/suggest', { params });
    return data.data;
  },
};
